import { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../../component/appLayout';
import { getChatGroupList } from '../../../services/chatMessageSyncService';
import { ChatGroupInterface } from '../../../interfaces/chatMessageInterface';
import { getUrlParam, isEmpty } from '../../../helpers/common';
import { useHistory } from 'react-router-dom';
import { captureException } from '../../../services/logService';
import MessageGroupCacheService from '../../../services/messageGroupCacheService';
import { preloadOpenChatMemory, refreshOpenChatGroupMessages } from '../../../services/conversationSyncService';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import Messagedetail from '../../../component/navbarMessageData/messageDetail';
import { Group, RightDiv } from './styles';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';

function MessagesDetails() {
  // States
  const [loading, setLoading] = useState(false);
  const [newGroup, setNewGroup] = useState(false);

  //mobx variables
  const { workspaceStore } = rootStore;
  const { currentWorkspace } = workspaceStore;

  // Redux state variables
  const { chatGroups, currentChatGroup } = rootStore.chatMessageStore;

  // Other variables

  const history = useHistory();

  // update current group
  const changeCurrentGroup = useCallback(
    (sortedData: ChatGroupInterface[]) => {
      const group = getUrlParam(history.location.search, 'chatGroup');
      if (sortedData?.length > 0 && isEmpty(group)) {
        const lastOpenGroupId = UserPreferenceSingleton.getInstance().getLastMessageGroup();
        const lastOpenGroup = sortedData?.find((x) => x?.id === lastOpenGroupId);
        if (lastOpenGroup) {
          rootStore.chatMessageStore.setCurrentChatGroup(lastOpenGroup);
        } else {
          rootStore.chatMessageStore.setCurrentChatGroup(sortedData[0]);
        }
      } else if (sortedData?.length > 0) {
        const currentGroup = sortedData?.find((x) => x?.id === group) || null;
        rootStore.chatMessageStore.setCurrentChatGroup(currentGroup);
      }
    },
    [history.location.search]
  );

  // load initial data
  const loadData = useCallback(
    async (isSetInitially?: boolean) => {
      try {
        if (!isEmpty(currentWorkspace?.id)) {
          setLoading(true);

          const newGroup = getUrlParam(history.location.search, 'newGroup');
          if (!isEmpty(newGroup)) {
            setNewGroup(true);
          }

          if (isSetInitially) {
            preloadOpenChatMemory();
            // Show the last known conversation list instantly while the fresh one loads.
            const cachedGroups = await MessageGroupCacheService.getInstance().getMessageGroups();
            if (cachedGroups?.length > 0) {
              rootStore.chatMessageStore.setChatGroups(cachedGroups);
            }
          }

          const previousGroupId = rootStore.chatMessageStore.currentChatGroup?.id;
          const groups = await getChatGroupList(!!isSetInitially);
          if (groups) {
            changeCurrentGroup(groups);
            const current = rootStore.chatMessageStore.currentChatGroup;
            if (current?.id && current.id === previousGroupId) {
              await refreshOpenChatGroupMessages(current.id, current.type, true);
            }
          }
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [currentWorkspace?.id, history.location.search, changeCurrentGroup]
  );

  useEffect(() => {
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id]);

  useEffect(() => {
    if (chatGroups && chatGroups.length > 0) {
      const groupIdFromUrl = getUrlParam(history.location.search, 'chatGroup');
      if (groupIdFromUrl) {
        // Using 'group' as the variable name
        const groupFromUrl = chatGroups.find((group) => group.id === groupIdFromUrl);

        if (groupFromUrl && groupFromUrl.id !== currentChatGroup?.id) {
          rootStore.chatMessageStore.setCurrentChatGroup(groupFromUrl);
        }
      } else {
        changeCurrentGroup(chatGroups);
      }
    }
  }, [chatGroups, history.location.search, currentChatGroup?.id, changeCurrentGroup]);

  // close new group
  const onCloseNewGroup = useCallback(() => {
    setNewGroup(false);
  }, []);

  // React to background message updates. The single App-level Firebase
  // 'messages' listener refreshes chatGroups in the store; refresh the open
  // conversation from it instead of running a duplicate listener here.
  useEffect(() => {
    if (currentChatGroup?.id) {
      const updatedCurrentGroup = chatGroups.find((group) => group?.id === currentChatGroup.id);
      if (updatedCurrentGroup) {
        rootStore.chatMessageStore.setCurrentChatGroup(updatedCurrentGroup);
      }
    }
  }, [chatGroups, currentChatGroup?.id]);

  useEffect(() => {
    return () => {
      rootStore.chatMessageStore.setCurrentChatGroup(null);
    };
  }, []);

  return (
    <AppLayout isMessageBox={true}>
      <Group>
        <RightDiv>
          <Messagedetail
            newGroup={newGroup}
            loadData={loadData}
            onCloseNewGroup={onCloseNewGroup}
            loading={loading}
            onClickNewChat={() => {}}
          />
        </RightDiv>
      </Group>
    </AppLayout>
  );
}

export default observer(MessagesDetails);
